import { formatCurrency } from "@/lib/formatting";
import { dealTotal, tradedPositions } from "@/lib/deals/positions";
import {
  positionPrice,
  positionQuantity,
  positionSubject,
  positionTotal,
} from "@/lib/deals/position-labels";
import type { DealPositionView } from "@/lib/deals/types";

/**
 * What a deal trades, position by position: each one's quantity in its unit, what it trades, its €
 * per unit and its worth, and, for several, their total. A deal that holds none (an invalidated
 * one) shows what it held.
 */
export function DealPositionsList({
  positions,
  withTotal = true,
}: {
  positions: readonly DealPositionView[];
  /** Whether the list closes with the deal's total; its caller may show it apart. */
  withTotal?: boolean;
}) {
  const traded = tradedPositions(positions);
  if (traded.length === 0) return null;
  const total = dealTotal(positions);
  return (
    <div data-testid="deal-positions">
      <ul className="space-y-1.5">
        {traded.map((position) => {
          const worth = positionTotal(position);
          return (
            <li key={position.id} className="flex items-baseline justify-between gap-3 text-sm">
              <span className="text-zinc-900">
                {positionQuantity(position)} · {positionSubject(position)}
              </span>
              <span className="text-right text-zinc-600">
                {positionPrice(position) ?? "Preis offen"}
                {worth != null && <> · {formatCurrency(worth)}</>}
              </span>
            </li>
          );
        })}
      </ul>
      {withTotal && traded.length > 1 && total != null && (
        <div className="mt-1.5 flex justify-between border-t border-zinc-200 pt-1.5 text-sm font-medium text-zinc-900">
          <span>Gesamtwert</span>
          <span>{formatCurrency(total)}</span>
        </div>
      )}
    </div>
  );
}
